import { effect, inject, signal } from '@angular/core';
import type { Account, Category } from '@app/domain/entities';
import { SETTINGS_REPOSITORY } from '@app/platform/tokens';
import { SantanderImportConfig } from './santander-import.models';
import { SANTANDER_SETTINGS } from './santander-setting-keys';

export class SantanderSettingsService {
  private readonly settings = inject(SETTINGS_REPOSITORY);

  readonly santanderEnabled = signal(this.settings.get<boolean>(SANTANDER_SETTINGS.IMPORT_ENABLED, false));

  constructor() {
    effect(() => {
      this.settings.set(SANTANDER_SETTINGS.IMPORT_ENABLED, this.santanderEnabled());
    });
  }

  enableSantander(): void {
    this.santanderEnabled.set(true);
  }

  disableSantander(): void {
    this.santanderEnabled.set(false);
  }

  getImportConfig(): SantanderImportConfig | undefined {
    return this.settings.get<SantanderImportConfig>(SANTANDER_SETTINGS.IMPORT_CONFIG);
  }

  saveImportConfig(config: SantanderImportConfig): void {
    this.settings.set(SANTANDER_SETTINGS.IMPORT_CONFIG, config);
  }

  normalizeImportConfig(
    config: SantanderImportConfig | undefined,
    accounts: readonly Account[],
    categories: readonly Category[],
  ): SantanderImportConfig | undefined {
    if (!config) {
      return undefined;
    }

    const visibleAccountIds = new Set(accounts.filter((account) => !account.hidden).map((account) => account.id));
    const visibleCategories = new Map(
      categories.filter((category) => !category.hidden).map((category) => [category.id, category] as const),
    );

    const normalized: SantanderImportConfig = {};

    if (config.accountId !== undefined && visibleAccountIds.has(config.accountId)) {
      normalized.accountId = config.accountId;
    }

    if (config.expenseCategoryId !== undefined && visibleCategories.has(config.expenseCategoryId)) {
      normalized.expenseCategoryId = config.expenseCategoryId;
    }

    if (
      normalized.expenseCategoryId !== undefined &&
      this.isValidChildCategory(normalized.expenseCategoryId, config.expenseSubcategoryId, visibleCategories)
    ) {
      normalized.expenseSubcategoryId = config.expenseSubcategoryId;
    }

    if (config.incomeCategoryId !== undefined && visibleCategories.has(config.incomeCategoryId)) {
      normalized.incomeCategoryId = config.incomeCategoryId;
    }

    if (
      normalized.incomeCategoryId !== undefined &&
      this.isValidChildCategory(normalized.incomeCategoryId, config.incomeSubcategoryId, visibleCategories)
    ) {
      normalized.incomeSubcategoryId = config.incomeSubcategoryId;
    }

    if (typeof config.allowReimportRecords === 'boolean') {
      normalized.allowReimportRecords = config.allowReimportRecords;
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private isValidChildCategory(
    parentId: number | undefined,
    childId: number | undefined,
    categories: ReadonlyMap<number, Category>,
  ): boolean {
    if (parentId === undefined || childId === undefined) {
      return false;
    }

    const child = categories.get(childId);
    return child?.parentId === parentId;
  }
}
